import { MetricsTracker, USER_EVENTS } from '@sm/metrics';

/**
 * Constants used for endpage metrics tracking.
 */
const PAGE_NAME = {
  SURVEY_TAKEN: 'survey taken',
  ENTERPRISE_THANK_YOU: 'enterprise thank you',
  SURVEY_CLOSED: 'survey closed',
  SURVEY_THANK_YOU: 'survey thank you',
  SURVEY_THANKS: 'survey thanks',
};

const ENDPAGE_USER_EVENTS = {
  PAGE_VIEW: 'page viewed',
  SURVEY_TAKEN: 'survey taken',
  SURVEY_CLOSED: 'survey closed',
  EXPERIMENT_EVENT: 'experiment event',
};

const PAGE_EVENTS = {
  [PAGE_NAME.SURVEY_TAKEN]: {
    amplitudeEvent: ENDPAGE_USER_EVENTS.SURVEY_TAKEN,
    pageName: PAGE_NAME.SURVEY_TAKEN,
  },
  [PAGE_NAME.ENTERPRISE_THANK_YOU]: {
    amplitudeEvent: ENDPAGE_USER_EVENTS.PAGE_VIEW,
    pageName: PAGE_NAME.ENTERPRISE_THANK_YOU,
  },
  [PAGE_NAME.SURVEY_CLOSED]: {
    amplitudeEvent: ENDPAGE_USER_EVENTS.SURVEY_CLOSED,
    pageName: PAGE_NAME.SURVEY_CLOSED,
  },
  [PAGE_NAME.SURVEY_THANK_YOU]: {
    amplitudeEvent: ENDPAGE_USER_EVENTS.EXPERIMENT_EVENT,
    pageName: PAGE_NAME.SURVEY_THANK_YOU,
  },
  [PAGE_NAME.SURVEY_THANKS]: {
    amplitudeEvent: ENDPAGE_USER_EVENTS.PAGE_VIEW,
    pageName: PAGE_NAME.SURVEY_THANKS,
  },
};

const CONSTANTS = {
  APP: 'respweb',
};

type SmParams = {
  surveyId?: string;
  collectorId?: string;
  respondentId?: string;
  categoryId?: string;
  languageId?: string;
};

type MetricsEventOptions = {
  pageName: string;
  pageVariant: string;
  smParams?: SmParams;
};

type LogEventOptions = {
  message: string;
  countryCode?: string;
  countryRegion?: string;
  languageId?: string;
};

type EventData = {
  name: USER_EVENTS;
  data: Record<string, unknown>;
};

/**
 * Generates event data for tracking page views and other events.
 * @param pageVariant - The template type or page variant
 * @param smParams - SurveyMonkey parameters (surveyId, collectorId, respondentId)
 * @param amplitudeEvent - The amplitude event name (optional, defaults to 'page viewed')
 * @param logMessage - Optional log message (defaults to pageVariant)
 */
function generateEventData(
  pageName: string,
  pageVariant = 'default',
  smParams?: SmParams,
  logMessage: string = pageVariant
): EventData {
  const pageEvent = PAGE_EVENTS[pageName];
  if (!pageEvent) {
    throw new Error(`Unknown pageName: ${pageName}`);
  }
  return {
    name: USER_EVENTS.PAGE_VIEW,
    data: {
      ...pageEvent,
      templateType: pageVariant,
      surveyId: smParams?.surveyId,
      collectorId: smParams?.collectorId,
      respondentId: smParams?.respondentId,
      languageId: smParams?.languageId,
      surveyCategoryId: smParams?.categoryId ?? 'None',
      urlPath: typeof window !== 'undefined' ? window.location.pathname : '',
      app: CONSTANTS.APP,
      log: {
        message: logMessage,
      },
    },
  };
}

/**
 * Sends a metrics event for a page view or other endpage event.
 * @param options - Object containing pageVariant and optional smParams
 */
function sendMetrics(options: MetricsEventOptions): void {
  const { pageName, pageVariant, smParams } = options;
  const eventData = generateEventData(pageName, pageVariant, smParams);
  MetricsTracker.track(eventData);
}

/**
 * Sends a log event for endpage tracking.
 * @param options - Object containing message, countryCode, countryRegion, and languageId
 */
function sendLog(options: LogEventOptions): void {
  const { message, countryCode, countryRegion, languageId } = options;
  MetricsTracker.track({
    name: USER_EVENTS.PAGE_VIEW,
    data: { log: { message, countryCode, countryRegion, languageId } },
  });
}

export { sendMetrics, sendLog, PAGE_NAME };
export type { SmParams };
